import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import Swal from 'sweetalert2';

/**
 * Known case-module route prefixes — the first path segment of any /:id case URL.
 * When the user hits a 403 on a GET request we navigate back to the module list.
 */
const CASE_MODULE_PREFIXES = [
  'loan-application',
  'opportunities',
  'fee-maker',
  'payment-maker',
  'fee-checker',
  'payment-checker',
  'quality-check-cpa',
  'quality-check-operations',
  'financial-analysis',
  'initiation',
  'capturing',
  'completion',
  'underwriter',
  'post-sanction-cases',
  'amendatory-sanction-cases',
  'disbursed-cases',
  'start-underwriting',
];

function isCaseModuleUrl(url: string): boolean {
  const firstSegment = url.split('/').filter(Boolean)[0] ?? '';
  return CASE_MODULE_PREFIXES.includes(firstSegment);
}

function resolveModuleListPath(url: string): string {
  const firstSegment = url.split('/').filter(Boolean)[0] ?? '';
  return firstSegment ? `/${firstSegment}` : '/dashboard';
}

const forbiddenToast = Swal.mixin({
  toast: true,
  position: 'top-end',
  icon: 'warning',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
});

/**
 * Global HTTP interceptor that handles 403 Forbidden responses on GET requests.
 *
 * When the backend rejects a case detail request (direct URL, browser history,
 * notification link) the user is redirected to the module list and shown a
 * user-friendly warning toast instead of a broken or empty page.
 *
 * Mutations (POST / PUT / PATCH / DELETE) are excluded — those are handled
 * per-module (e.g. loanApplicationApiFeedbackInterceptor).
 */
export const forbiddenInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }

  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 403) {
        const currentUrl = router.url;
        if (isCaseModuleUrl(currentUrl)) {
          const listPath = resolveModuleListPath(currentUrl);
          void forbiddenToast.fire({
            title: 'Access restricted',
            text: readForbiddenMessage(error),
          });
          void router.navigate([listPath]);
        }
      }
      return throwError(() => error);
    }),
  );
};

function readForbiddenMessage(error: HttpErrorResponse): string {
  const body = error.error;
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'You do not have access to this case. Please contact your supervisor.';
}
